import React from 'react';
import { useTranslation } from 'react-i18next';
import ProgressBar from '../design/ProgressBar';
import StatTile from '../design/StatTile';
import { statusFromScore } from '../utils/pasuk';
import type { Student, StudentProgress } from '../types';

interface ClassSummaryProps {
  students: Student[];
  progressByStudent: Record<string, StudentProgress>;
  currentKey: string | null;
}

/** Class-wide numbers for the current pasuk. */
const ClassSummary: React.FC<ClassSummaryProps> = ({ students, progressByStudent, currentKey }) => {
  const { t } = useTranslation();

  const current = students.map((s) =>
    currentKey ? progressByStudent[s.id]?.pesukim[currentKey] : undefined,
  );
  const practiced = current.filter((p) => p && p.attempts > 0);
  const passed = current.filter((p) => {
    const status = statusFromScore(p?.bestScore, p?.attempts ?? 0);
    return status === 'excellent' || status === 'good';
  });
  const avg =
    practiced.length > 0
      ? Math.round(practiced.reduce((sum, p) => sum + (p?.bestScore ?? 0), 0) / practiced.length)
      : null;
  const passedPct = students.length > 0 ? (passed.length / students.length) * 100 : 0;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile label={t('school_stat_students')} value={students.length} />
      <StatTile
        label={t('school_stat_practiced')}
        value={t('school_x_of_y', { x: practiced.length, y: students.length })}
      />
      <StatTile
        label={t('school_stat_passed')}
        value={t('school_x_of_y', { x: passed.length, y: students.length })}
        hint={t('school_stat_passed_hint')}
      >
        <ProgressBar value={passedPct} label={t('school_stat_passed') ?? ''} />
      </StatTile>
      <StatTile label={t('school_stat_avg')} value={avg ?? '—'} hint={t('school_stat_avg_hint')} />
    </div>
  );
};

export default ClassSummary;
